import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '@theme';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button, Card } from '@/components/common';
import { FiCreditCard, FiDownload, FiCheck, FiTrendingUp } from 'react-icons/fi';

const PageHeader = styled.div`
  margin-bottom: ${theme.spacing.xl};
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.sizes['3xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

const PageSubtitle = styled.p`
  font-size: ${theme.typography.sizes.base};
  color: ${theme.colors.text}80;
  margin: 0;
`;

const Section = styled.div`
  margin-bottom: ${theme.spacing['2xl']};
`;

const SectionTitle = styled.h2`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.lg} 0;
`;

const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xl};
`;

const PlanCard = styled(Card)<{ isActive?: boolean; isPopular?: boolean }>`
  position: relative;
  padding: ${theme.spacing.xl};
  border: 2px solid
    ${props =>
      props.isActive
        ? theme.colors.primary
        : props.isPopular
        ? theme.colors.accent
        : 'transparent'};
  transition: all ${theme.transitions.normal};

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${theme.shadows.xl};
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: -12px;
  right: ${theme.spacing.lg};
  background: ${theme.colors.accent};
  color: ${theme.colors.white};
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weights.semibold};
  text-transform: uppercase;
`;

const ActiveBadge = styled(PopularBadge)`
  background: ${theme.colors.primary};
`;

const PlanName = styled.h3`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

const PlanPrice = styled.div`
  font-size: ${theme.typography.sizes['3xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.xs};
`;

const PlanPeriod = styled.div`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}60;
  margin-bottom: ${theme.spacing.lg};
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 ${theme.spacing.xl} 0;
`;

const Feature = styled.li`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm} 0;
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}80;

  svg {
    color: ${theme.colors.success};
    flex-shrink: 0;
  }
`;

const InvoiceTable = styled.table`
  width: 100%;
  background: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${theme.shadows.md};
`;

const TableHeader = styled.thead`
  background: ${theme.colors.surface};
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${theme.colors.surface};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${theme.colors.surface}50;
  }
`;

const TableHead = styled.th`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  text-align: left;
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text}80;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableCell = styled.td`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};
`;

const StatusBadge = styled.span<{ status: 'paid' | 'pending' | 'failed' }>`
  display: inline-flex;
  align-items: center;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weights.medium};
  background: ${props => {
    switch (props.status) {
      case 'paid':
        return theme.colors.success + '20';
      case 'pending':
        return theme.colors.warning + '20';
      case 'failed':
        return theme.colors.error + '20';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'paid':
        return theme.colors.success;
      case 'pending':
        return theme.colors.warning;
      case 'failed':
        return theme.colors.error;
    }
  }};
`;

const DownloadButton = styled.button`
  background: transparent;
  border: none;
  color: ${theme.colors.primary};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  font-size: ${theme.typography.sizes.sm};
  transition: color ${theme.transitions.fast};

  &:hover {
    color: ${theme.colors.accent};
  }
`;

const plans = [
  {
    id: 'starter',
    name: 'Starter',
    price: '99 FCFA',
    period: '/mois',
    features: [
      '100 scorings/mois',
      '5 GB de stockage',
      '5 utilisateurs',
      'Support email',
      'API basique',
    ],
    isActive: false,
    isPopular: false,
  },
  {
    id: 'professional',
    name: 'Professional',
    price: '299 FCFA',
    period: '/mois',
    features: [
      '500 scorings/mois',
      '20 GB de stockage',
      '15 utilisateurs',
      'Support prioritaire',
      'API avancée',
      'Webhooks',
      'Exports personnalisés',
    ],
    isActive: true,
    isPopular: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: '999 FCFA',
    period: '/mois',
    features: [
      'Scorings illimités',
      '100 GB de stockage',
      'Utilisateurs illimités',
      'Support 24/7',
      'API complète',
      'Webhooks avancés',
      'White label',
      'SLA garanti',
      'Manager dédié',
    ],
    isActive: false,
    isPopular: false,
  },
];

const invoices = [
  {
    id: 'INV-2024-001',
    date: '01/01/2024',
    amount: '299 FCFA',
    status: 'paid' as const,
    plan: 'Professional',
  },
  {
    id: 'INV-2023-012',
    date: '01/12/2023',
    amount: '299 FCFA',
    status: 'paid' as const,
    plan: 'Professional',
  },
  {
    id: 'INV-2023-011',
    date: '01/11/2023',
    amount: '299 FCFA',
    status: 'paid' as const,
    plan: 'Professional',
  },
  {
    id: 'INV-2023-010',
    date: '01/10/2023',
    amount: '99 FCFA',
    status: 'paid' as const,
    plan: 'Starter',
  },
];

export const Billing: React.FC = () => {
  const [selectedPlan, setSelectedPlan] = useState('professional');

  const handleUpgrade = (planId: string) => {
    setSelectedPlan(planId);
    // TODO: Implement upgrade logic
    console.log('Upgrading to:', planId);
  };

  return (
    <DashboardLayout>
      <PageHeader>
        <PageTitle>Facturation</PageTitle>
        <PageSubtitle>Gérez votre abonnement et consultez vos factures</PageSubtitle>
      </PageHeader>

      <Section>
        <SectionTitle>Plans disponibles</SectionTitle>
        <PlansGrid>
          {plans.map(plan => (
            <PlanCard key={plan.id} isActive={plan.isActive} isPopular={plan.isPopular}>
              {plan.isActive && <ActiveBadge>Plan actuel</ActiveBadge>}
              {plan.isPopular && !plan.isActive && <PopularBadge>Populaire</PopularBadge>}

              <PlanName>{plan.name}</PlanName>
              <PlanPrice>{plan.price}</PlanPrice>
              <PlanPeriod>{plan.period}</PlanPeriod>

              <FeatureList>
                {plan.features.map((feature, index) => (
                  <Feature key={index}>
                    <FiCheck />
                    {feature}
                  </Feature>
                ))}
              </FeatureList>

              <Button
                variant={plan.isActive ? 'secondary' : 'primary'}
                fullWidth
                disabled={plan.isActive}
                onClick={() => handleUpgrade(plan.id)}
              >
                {plan.isActive ? 'Plan actuel' : 'Choisir ce plan'}
              </Button>
            </PlanCard>
          ))}
        </PlansGrid>
      </Section>

      <Section>
        <SectionTitle>Historique des factures</SectionTitle>
        <InvoiceTable>
          <TableHeader>
            <TableRow>
              <TableHead>Numéro</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead>Montant</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <tbody>
            {invoices.map(invoice => (
              <TableRow key={invoice.id}>
                <TableCell>{invoice.id}</TableCell>
                <TableCell>{invoice.date}</TableCell>
                <TableCell>{invoice.plan}</TableCell>
                <TableCell>{invoice.amount}</TableCell>
                <TableCell>
                  <StatusBadge status={invoice.status}>
                    {invoice.status === 'paid' ? 'Payée' : 'En attente'}
                  </StatusBadge>
                </TableCell>
                <TableCell>
                  <DownloadButton>
                    <FiDownload />
                    Télécharger
                  </DownloadButton>
                </TableCell>
              </TableRow>
            ))}
          </tbody>
        </InvoiceTable>
      </Section>
    </DashboardLayout>
  );
};
